import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CreateSampleModal from "@/components/CreateSampleModal";
import { useAuth } from "@/hooks/useAuth";
import { useAnalysisList } from "@/hooks/useAnalysis";
import { useCaseStats, useCreateCase } from "@/hooks/useCases";
import { useCreateSample } from "@/hooks/useSamples";
import type { Case, CaseType, SampleType } from "@/types";
import {
  analysisStatusColor,
  analysisStatusLabels,
  analysisTypeLabels,
  caseStatusColor,
  caseStatusLabels,
  caseTypeLabels,
} from "@/utils/labels";

function SkeletonCard() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
      <div className="h-8 bg-gray-200 rounded w-1/3" />
    </div>
  );
}

export default function DashboardPage() {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const { data: stats, isLoading, error } = useCaseStats();
  const { data: recentAnalyses } = useAnalysisList({ limit: 5 });
  const createCase = useCreateCase();
  const createSample = useCreateSample();

  // B3: 모달 상태
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [caseForm, setCaseForm] = useState({
    case_number: "",
    title: "",
    case_type: "fire" as CaseType,
  });

  const handleCreateCase = async () => {
    if (!caseForm.case_number.trim() || !caseForm.title.trim()) {
      alert("사건번호와 제목은 필수 입력 항목입니다.");
      return;
    }
    try {
      const result = await createCase.mutateAsync(caseForm);
      setCaseForm({ case_number: "", title: "", case_type: "fire" });
      setShowCaseModal(false);
      navigate(`/cases/${result.id}`);
    } catch {
      alert("사건 등록에 실패했습니다.");
    }
  };

  const handleCreateSample = async (data: {
    sample_id: string;
    name: string;
    sample_type: SampleType;
    case_id: number;
  }) => {
    try {
      const result = await createSample.mutateAsync(data);
      setShowSampleModal(false);
      navigate(`/samples/${result.id}`);
    } catch {
      alert("시료 등록에 실패했습니다.");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">대시보드</h1>
          <p className="text-gray-600">
            주요 통계 및 현황을 한눈에 확인합니다.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/4 mb-6" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-200 rounded mb-3" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">대시보드</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(error as Error).message}
          </p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: "전체 사건",
      value: stats?.total_cases ?? 0,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "접수 / 진행중",
      value: `${stats?.open_cases ?? 0} / ${stats?.in_progress_cases ?? 0}`,
      color: "text-yellow-600",
      bg: "bg-yellow-50",
    },
    {
      label: "종결 사건",
      value: stats?.closed_cases ?? 0,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      label: "시료 / 분석",
      value: `${stats?.total_samples ?? 0} / ${stats?.total_analyses ?? 0}`,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">대시보드</h1>
        <p className="text-gray-600">주요 통계 및 현황을 한눈에 확인합니다.</p>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => (
          <div
            key={card.label}
            className={`rounded-lg shadow-sm border border-gray-200 p-6 ${card.bg}`}
          >
            <p className="text-sm font-medium text-gray-600 mb-1">
              {card.label}
            </p>
            <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* 최근 사건 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">최근 사건</h2>
            <p className="text-sm text-gray-500 mt-1">최근 등록된 사건 목록</p>
          </div>
          <Link
            to="/cases"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            전체 보기
          </Link>
        </div>

        {stats?.recent_cases && stats.recent_cases.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사건번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    등록일
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.recent_cases.map((c: Case) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/cases/${c.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {c.case_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {c.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {caseTypeLabels[c.case_type] ?? c.case_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${caseStatusColor(c.status)}`}
                      >
                        {caseStatusLabels[c.status] ?? c.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(c.created_at).toLocaleDateString("ko-KR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-gray-500">등록된 사건이 없습니다.</p>
            <Link
              to="/cases"
              className="mt-2 inline-block text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              사건 등록하기
            </Link>
          </div>
        )}
      </div>

      {/* 최근 분석 현황 + 빠른 실행 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* 최근 분석 현황 */}
        <div className="bg-white shadow rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              최근 분석 현황
            </h3>
            <Link
              to="/analyses"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              전체 보기
            </Link>
          </div>
          {recentAnalyses && recentAnalyses.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {recentAnalyses.map((analysis) => (
                <div
                  key={analysis.id}
                  className="px-6 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <Link
                      to={`/analyses/${analysis.id}`}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      #{analysis.id}
                    </Link>
                    <span className="text-sm text-gray-600">
                      {analysisTypeLabels[analysis.analysis_type] ??
                        analysis.analysis_type}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${analysisStatusColor(analysis.status)}`}
                    >
                      {analysisStatusLabels[analysis.status] ?? analysis.status}
                    </span>
                    {analysis.status === "completed" && (
                      <Link
                        to={`/analyses/${analysis.id}/dashboard`}
                        className="text-xs text-green-600 hover:text-green-800 font-medium"
                      >
                        대시보드
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500 text-sm">
              최근 분석 내역이 없습니다.
            </div>
          )}
        </div>

        {/* 빠른 실행 (B3: 모달 기반 인라인 등록) */}
        <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">빠른 실행</h3>
          <div className="space-y-3">
            {permissions?.canCreateCases && (
              <button
                type="button"
                onClick={() => setShowCaseModal(true)}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">사건 등록</div>
                <div className="text-sm text-gray-500">
                  새 사건을 등록합니다.
                </div>
              </button>
            )}
            {permissions?.canCreateSamples && (
              <button
                type="button"
                onClick={() => setShowSampleModal(true)}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">시료 등록</div>
                <div className="text-sm text-gray-500">
                  시료를 등록하고 파일을 업로드합니다.
                </div>
              </button>
            )}
            {permissions?.canRunAnalysis && (
              <Link
                to="/analyses"
                className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">분석 시작</div>
                <div className="text-sm text-gray-500">
                  AI 분석을 실행합니다.
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* B3: 사건 등록 모달 */}
      {showCaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowCaseModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              사건 등록
            </h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  사건번호 *
                </label>
                <input
                  type="text"
                  value={caseForm.case_number}
                  onChange={(e) =>
                    setCaseForm({ ...caseForm, case_number: e.target.value })
                  }
                  placeholder="예: CASE-2025-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제목 *
                </label>
                <input
                  type="text"
                  value={caseForm.title}
                  onChange={(e) =>
                    setCaseForm({ ...caseForm, title: e.target.value })
                  }
                  placeholder="사건 제목"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  사건 유형
                </label>
                <select
                  value={caseForm.case_type}
                  onChange={(e) =>
                    setCaseForm({
                      ...caseForm,
                      case_type: e.target.value as CaseType,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(caseTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCaseModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={createCase.isPending}
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleCreateCase}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={createCase.isPending}
              >
                {createCase.isPending ? "등록 중..." : "사건 등록"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* B3: 시료 등록 모달 (사건 선택 포함) */}
      <CreateSampleModal
        caseId={0}
        caseName=""
        isOpen={showSampleModal}
        onClose={() => setShowSampleModal(false)}
        onSubmit={handleCreateSample}
        isLoading={createSample.isPending}
      />
    </div>
  );
}
